import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import { useCreateVaultDocumentsBatchMutation } from "#graphql/supergraphHooks";
import {
  type CreateVaultDocumentsBatchInput,
  type CreateVaultDocumentsBatchMutation,
  type CreateVaultDocumentsBatchMutationVariables,
} from "#graphql/supergraphTypes";

export interface UseCreateVaultDocumentsBatchReturn {
  createVaultDocumentsBatch: (
    input: CreateVaultDocumentsBatchInput,
  ) => Promise<CreateVaultDocumentsBatchMutation["createVaultDocumentsBatch"] | null>;
  loading: boolean;
  error: ReturnType<typeof useCreateVaultDocumentsBatchMutation>[1]["error"];
}

/**
 * DV-250 — step 1 of the BULK upload path: create a batch of document rows in one
 * round trip and mint one single-part presigned upload target per document.
 * Returns `{ documents, uploadTargets }` (paired by index), or `null` if the
 * mutation returned no data. Usually driven via {@link useUploadVaultDocumentsBatch}.
 */
export const useCreateVaultDocumentsBatch = (
  options?: MutationHookOptions<
    CreateVaultDocumentsBatchMutation,
    CreateVaultDocumentsBatchMutationVariables
  >,
): UseCreateVaultDocumentsBatchReturn => {
  const [mutate, { loading, error }] = useCreateVaultDocumentsBatchMutation(options);

  const createVaultDocumentsBatch = useCallback(
    async (input: CreateVaultDocumentsBatchInput) => {
      const { data } = await mutate({ variables: { input } });
      return data?.createVaultDocumentsBatch ?? null;
    },
    [mutate],
  );

  return { createVaultDocumentsBatch, loading, error };
};
